import React, { useMemo, useState } from 'react';
import DefaultLayout from '@/layout/DefaultLayout';
import Breadcrumb from '@/components/Breadcrumb';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import DynamicTable, { SelectColumnFilter } from '@/components/DynamicTable';
import Pagination from '@/components/Pagination';
import Spinner from '@/components/Spinner';
import { DeleteButton, EditButton } from '@/components/shared/Button';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal';
import {
  SupplierOrdersResponse,
  deleteSupplierOrder,
  getSupplierOrders,
} from '@/services/SupplierOrderService';
import { useTranslation } from 'react-i18next';
import { OfferStatus } from '@/enums/OfferStatus';
import { PaymentStatus } from '@/enums/PaymentStatus';
import { stateColors } from '@/enums/RequestLeaveState';
import moment from 'moment';
import { Currency } from '@/enums/Currency';

function SupplierOrders() {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState<string>('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState('');
  const navigate = useNavigate();

  const { t } = useTranslation();

  const ActionButtonsCell = ({ value }: any) => {
    return (
      <div>
        <DeleteButton
          onClick={() => {
            setSelectedRow(value);
            setIsModalVisible(true);
          }}
        >
          {t('SupplierOrders.deleteButton')}
        </DeleteButton>

        <EditButton
          type="button"
          onClick={() => {
            navigate(`${value}`);
          }}
        >
          {t('SupplierOrders.editButton')}
        </EditButton>
      </div>
    );
  };

  const handleCancelDelete = () => {
    setIsModalVisible(false);
  };

  const handleDeleteConfirmation = () => {
    handleDeleteSupplierOrder.mutate(selectedRow);
    setIsModalVisible(false);
  };

  const columns = useMemo(
    () => [
      {
        Header: t('SupplierOrders.actionsColumn'),
        accessor: 'id',
        Cell: ({ cell: { value } }: any) => (
          <ActionButtonsCell
            value={value}
            handleDelete={handleDeleteSupplierOrder}
          />
        ),
      },
      {
        Header: t('SupplierOrders.supplierColumn'),
        accessor: 'supplier',
        Filter: SelectColumnFilter,
      },
      {
        Header: t('SupplierOrders.contactColumn'),
        accessor: 'contact',
      },
      {
        Header: t('SupplierOrders.amountHTColumn'),
        accessor: 'amountHT',
        Cell: ({ row, cell: { value } }) => {
          const currencyTxt = Currency[row.original['currency']];
          return <div>{value + ' ' + currencyTxt}</div>;
        },
      },
      {
        Header: t('SupplierOrders.amountTTCColumn'),
        accessor: 'amountTTC',
        Cell: ({ row, cell: { value } }) => {
          const currencyTxt = Currency[row.original['currency']];
          return <div>{value + ' ' + currencyTxt}</div>;
        },
      },
      {
        Header: t('SupplierOrders.submissionDateColumn'),
        accessor: 'submissionDate',
        Cell: ({ cell: { value } }) => {
          const formattedValue = moment.utc(value).format('YYYY-MM-DD');
          return (
            <input
              type="text"
              className="min-w-40 dark:bg-boxdark-2 dark:text-bodydark"
              value={formattedValue}
              readOnly
            />
          );
        },
      },
      {
        Header: t('SupplierOrders.offerStatusColumn'),
        accessor: 'offerStatus',
        Cell: ({ cell: { value } }) => {
          const frontendState = OfferStatus[value];
          const bgColor = stateColors[frontendState] || 'bg-gray-300';

          return (
            <div
              className={`min-w-50 w-40 rounded-md text-center ${bgColor} px-2.5  py-2.5 text-sm text-white`}
            >
              {frontendState !== null ? t(`OfferStatus.${frontendState}`) : ''}
            </div>
          );
        },
      },
      {
        Header: t('SupplierOrders.paymentStatusColumn'),
        accessor: 'paymentStatus',
        Cell: ({ cell: { value } }) => {
          const frontendState = PaymentStatus[value];
          const bgColor = stateColors[frontendState] || 'bg-gray-300';

          return (
            <div
              className={`min-w-50 w-40 rounded-md text-center ${bgColor} px-2.5  py-2.5 text-sm text-white`}
            >
              {frontendState !== null
                ? t(`PaymentStatus.${frontendState}`)
                : ''}
            </div>
          );
        },
      },
    ],
    [t]
  );

  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    data: supplierOrders,
  } = useQuery<SupplierOrdersResponse | undefined>(
    ['supplierOrders', pageIndex, pageSize, search],
    () =>
      getSupplierOrders({
        fromvalue: pageIndex,
        takevalue: pageSize,
        search: search,
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: 6000,
      keepPreviousData: true,
    }
  );

  const handleDeleteSupplierOrder = useMutation(
    async (id: string) => {
      await deleteSupplierOrder(id);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'supplierOrders',
          pageIndex,
          pageSize,
          search,
        ]);
      },
    }
  );

  const pageCount = useMemo(() => {
    return Math.ceil((supplierOrders?.count ?? 0) / pageSize);
  }, [pageSize, supplierOrders]);
  const nextPage = () => setPageIndex(pageIndex + 1);
  const previousPage = () => setPageIndex(pageIndex - 1);
  const onPageChange = (newPageIndex: number) => setPageIndex(newPageIndex);
  const onPageSizeChange = (newPageSize: number) => {
    setPageIndex(0);
    setPageSize(newPageSize);
  };

  return (
    <DefaultLayout>
      <Breadcrumb
        pageName={t('SupplierOrders.pageName')}
        addbtn={true}
        addRouter="new"
      />
      {isLoading ? (
        <div className="flex items-center justify-center">
          <Spinner height="3rem" width="3rem" />
        </div>
      ) : (
        <div className="flex flex-col">
          <DynamicTable
            dataCount={supplierOrders?.count}
            data={supplierOrders?.supplierOrders ?? []}
            columns={columns}
            setSearch={setSearch}
            exportPdf={false}
          />
          {isModalVisible && (
            <DeleteConfirmationModal
              isOpen={isModalVisible}
              onDelete={handleDeleteConfirmation}
              onCancel={handleCancelDelete}
            />
          )}
          <Pagination
            pageIndex={pageCount === 0 ? -1 : pageIndex}
            pageCount={pageCount}
            pageSize={pageSize}
            onNextPage={nextPage}
            onPreviousPage={previousPage}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
    </DefaultLayout>
  );
}

export default SupplierOrders;
